<template>
  <div>
    <div class="row mb-3">
      <div class="col-12 mb-2">
        <b-form-radio-group
          v-model="mapMode"
          :options="mapModeOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          @input="handleMapModeChange"
        />
      </div>

      <div class="form-group col-md-3">
        <label for="centrals">{{ $t("Centrals") }}:</label>
        <v-select
          :options="centerOptions"
          :reduce="(item) => item.id"
          label="name"
          v-model="filters.center_id"
          class="vselect"
          @input="handleCenterChange"
          :loading="loadingStates.centers"
        />
      </div>

      <div class="form-group col-md-3">
        <label for="unit">{{ $t("Unit") }}:</label>
        <v-select
          :options="unitOptions"
          :reduce="(item) => item.id"
          label="name"
          v-model="filters.unit_id"
          class="vselect"
          :disabled="!filters.center_id"
          @input="handleUnitChange"
          :loading="loadingStates.units"
        />
      </div>

      <div class="form-group col-md-3" v-if="!isDeviceMode">
        <label for="vehicleType">{{ $t("Vehicle type") }}:</label>
        <v-select
          :options="vehicleTypeOptions"
          :reduce="(item) => item.code"
          label="name"
          v-model="filters.vehicle_type_code"
          class="vselect"
          @input="handleVehicleTypeChange"
          :loading="loadingStates.vehicles"
        />
      </div>

      <div class="form-group col-md-3" v-if="!isDeviceMode">
        <label for="filter">{{ $t("Filter by vehicle") }}:</label>
        <v-select
          :options="vehicleOptions"
          :reduce="(item) => item.id"
          label="name"
          v-model="filters.vehicle_id"
          class="vselect"
          @input="handleVehicleChange"
          :loading="loadingStates.vehicles"
        />
      </div>

      <div class="form-group col-md-3" v-if="isDeviceMode">
        <label for="deviceType">{{ $t("Device type") }}:</label>
        <v-select
          :options="deviceTypeOptions"
          :reduce="(item) => item.id"
          label="name"
          v-model="filters.device_type_id"
          class="vselect"
          @input="handleDeviceFilterChange"
          :loading="loadingStates.deviceTypes"
        />
      </div>

      <div class="form-group col-md-3" v-if="isDeviceMode">
        <label for="deviceFilter">{{ $t("Filter by device") }}:</label>
        <v-select
          :options="deviceOptions"
          :reduce="(item) => item.id"
          label="label"
          v-model="filters.device_id"
          class="vselect"
          @input="handleDeviceFilterChange"
          :loading="loadingStates.devices"
        />
      </div>

      <div class="form-group col-md-3" v-if="!isDeviceMode">
          <label for="filter">Pontos de referência </label>
          <v-select
            :options="landmarkTypeOptions"
            :reduce="(item) => item.id"
            label="name"
            v-model="filters.landmark_type_ids"
            class="vselect"
            @input="filterLandmarksByType"
            :loading="loadingStates.landmarks"
            multiple
            placeholder="Selecione um ou mais tipos"
          />
      </div>

      <div class="col-12">
        <MapPositionNowPointsList
          ref="mapList"
          :zoomMapa="mapZoom"
          :vehicleFleet="vehicleFleet"
          :selectedVehicleId="filters.vehicle_id"
          :landmarkMarkers="landmarkMarkers"
          :deviceFleet="deviceFleet"
          :mapMode="mapMode"
          :enableCameraButton="true"
          :enableDevicesButton="true"
          @reload="loadVehicleFleet"
          @open-camera="openCameraModal"
          @open-devices="openDevicesModal"
        />
      </div>
    </div>

    <b-modal
        id="camera-modal"
        size="xl"
        hide-footer
        :title="$t('Cameras')"
        @hidden="onCameraModalHidden"
    >
      <VehicleCameras v-if="cameraModalVehicleId" :vehicleId="cameraModalVehicleId" />
    </b-modal>

    <b-modal
        id="devices-modal"
        size="xl"
        hide-footer
        :title="$t('Devices')"
        @hidden="onDevicesModalHidden"
    >
      <VehicleDevicesMap v-if="devicesModalVehicleId" :vehicleId="devicesModalVehicleId" />
    </b-modal>
  </div>
</template>

<script>
import MapPositionNowPointsList from "../layouts/Mapa/MapPositionNowPointsList.vue";
import VehicleCameras from "./Vehicle/components/VehicleCameras.vue";
import VehicleDevicesMap from "./Vehicle/components/VehicleDevicesMap.vue";
import axios from "axios";

export default {
  name: "Fleet",

  components: {
    MapPositionNowPointsList,
    VehicleCameras,
    VehicleDevicesMap,
  },

  data() {
    return {
      mapMode: "vehicles",
      filters: {
        center_id: null,
        unit_id: null,
        vehicle_id: null,
        vehicle_type_code: null,
        device_id: null,
        device_type_id: null,
        landmark_type_ids: [],
      },
      loadingStates: {
        centers: false,
        units: false,
        vehicles: false,
        fleet: false,
        vehicle_types: false,
        landmarks: false,
        devices: false,
        deviceTypes: false,
      },
      centers: [],
      units: [],
      vehicles: [],
      vehicleTypes: [],
      vehicleFleet: [],
      mapZoom: 13,
      landmarkMarkers: [],
      allLandmarks: [],
      landmarkTypes: [],
      lastUpdateListener: null,
      cameraModalVehicleId: null,
      devicesModalVehicleId: null,
      deviceFleet: [],
      deviceTypes: [],
      devices: [],
      devicePositionListener: null,
    };
  },

  computed: {
    isDeviceMode() {
      return this.mapMode === "devices";
    },

    mapModeOptions() {
      return [
        { value: "vehicles", text: this.$t("Vehicles") },
        { value: "devices", text: this.$t("Devices") },
      ];
    },

    deviceTypeOptions() {
      return this.deviceTypes.map((type) => ({ id: type.id, name: type.name }));
    },

    deviceOptions() {
      return this.devices.map((device) => ({ id: device.id, label: device.label }));
    },

    companyId() {
      return this.$store.state.user.company_id > 1
        ? this.$store.state.user.company_id
        : null;
    },

    centerId() {
      return this.$store.state.user.center_id > 1
        ? this.$store.state.user.center_id
        : null;
    },

    unitId() {
      return this.$store.state.user.unit_id > 1
        ? this.$store.state.user.unit_id
        : null;
    },

    centerOptions() {
      return [{ id: null, name: "Todos" }, ...this.centers];
    },

    unitOptions() {
      return [{ id: null, name: "Todos" }, ...this.units];
    },

    landmarkTypeOptions() {
      return (this.landmarkTypes || []).map(type => ({
        id: type.id,
        name: type.name
      }));
    },

    vehicleTypeOptions() {
      const list = (this.vehicleTypes || [])
        .filter((t) => t && (Number(t.status) === 1 || t.status === true))
        .map((t) => ({
          code: String(t.sigla || "").toUpperCase() || null,
          name:
            (t.sigla ? String(t.sigla).toUpperCase() : "") +
            (t.name ? ` - ${t.name}` : ""),
        }))
        .filter((x) => x.code);
      return [{ code: null, name: "Todos" }, ...list];
    },

    vehicleOptions() {
      let filtered = (this.vehicles || []).filter(
        (vehicleData) =>
          vehicleData &&
          vehicleData.vehicle &&
          vehicleData.vehicle.status === 1,
      );

      if (this.filters.center_id || this.filters.unit_id) {
        filtered = filtered.filter((vehicleData) => {
          const vehicle = vehicleData.vehicle;
          if (!vehicle) return false;
          if (this.filters.unit_id) {
            return Number(vehicle.unit_id) === Number(this.filters.unit_id);
          }
          if (this.filters.center_id) {
            return Number(vehicle.center_id) === Number(this.filters.center_id);
          }
          return true;
        });
      }

      const formattedVehicles = filtered.map((vehicleData) => ({
        id: vehicleData.vehicle.id,
        name: vehicleData.vehicle.name,
        status: vehicleData.vehicle.status,
      }));

      return [
        { id: null, name: "Todos" },
        ...new Map(
          formattedVehicles.map((vehicle) => [vehicle.id, vehicle]),
        ).values(),
      ];
    },
  },

  methods: {
    openDevicesModal(vehicleId) {
      this.devicesModalVehicleId = vehicleId;
      this.$bvModal.show('devices-modal');
    },

    onDevicesModalHidden() {
      this.devicesModalVehicleId = null;
    },

    openCameraModal(vehicleId) {
      this.cameraModalVehicleId = vehicleId;
      this.$bvModal.show('camera-modal');
    },

    onCameraModalHidden() {
      this.cameraModalVehicleId = null;
    },

    async loadLandmarkTypes() {
      this.loadingStates.landmarks = true;
      try {
        const resp = await axios.get(`${this.$store.state.api}landmark_types`);
        this.landmarkTypes = Array.isArray(resp.data) ? resp.data : (resp.data.data || []);
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.landmarks = false;
      }
    },

    async loadLandmarks() {
      this.loadingStates.landmarks = true;
      try {
        const params = {
          paginated: false,
          destino_situacao: 1,
          ...(this.filters.center_id && { center_id: this.filters.center_id }),
          ...(this.filters.unit_id && { unit_id: this.filters.unit_id }),
        };
        const response = await axios.get(`${this.$store.state.api}landmarks`, { params });
        const typesMap = Object.fromEntries((this.landmarkTypes || []).map(t => [t.id, t]));
        this.allLandmarks = (response.data.data || []).map(l => ({
          ...l,
          landmark_type: typesMap[l.tipo_destino_id] || null
        }));
        this.filterLandmarksByType();
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.landmarks = false;
      }
    },

    filterLandmarksByType() {
      const selectedIds = this.filters.landmark_type_ids;
      if (!selectedIds || selectedIds.length === 0) {
        this.landmarkMarkers = [];
      } else {
        this.landmarkMarkers = this.allLandmarks.filter(l => 
          selectedIds.includes(l.tipo_destino_id)
        );
      }
    },

    getRecordVehicleId(record) {
      if (!record) return null;
      const fromVehicle =
        record.vehicle && record.vehicle.id != null
          ? Number(record.vehicle.id)
          : null;
      const fromVehicleId =
        record.vehicle_id != null ? Number(record.vehicle_id) : null;
      const fromId = record.id != null ? Number(record.id) : null;
      return (
        [fromVehicle, fromVehicleId, fromId].find(
          (v) => v !== null && !Number.isNaN(v),
        ) ?? null
      );
    },

    async handleCenterChange(centerId) {
      this.filters.unit_id = null;
      this.filters.vehicle_id = null;
      this.filters.vehicle_type_code = null;

      if (centerId) {
        await this.loadUnits(centerId);
      } else {
        this.units = [];
      }

      await this.reloadCurrentMode();

      if (!this.isDeviceMode) {
        await this.loadLandmarks();
      }

      this.reframeMap();
    },

    async handleUnitChange() {
      this.filters.vehicle_id = null;
      this.filters.vehicle_type_code = null;
      this.filters.device_id = null;
      await this.reloadCurrentMode();

      if (!this.isDeviceMode) {
        await this.loadLandmarks();
      }

      this.reframeMap();
    },

    handleVehicleTypeChange() {
      this.filters.vehicle_id = null;
      this.loadVehicleFleet();
      this.loadLandmarks();
      this.reframeMap();
    },

    handleVehicleChange() {
      this.loadVehicleFleet();
      this.loadLandmarks();
      this.reframeMap();
    },

    async reloadCurrentMode() {
      if (this.isDeviceMode) {
        await this.loadDeviceFleet();
        return;
      }

      await this.loadVehicleFleet();
    },

    async handleMapModeChange() {
      this.filters.vehicle_id = null;
      this.filters.vehicle_type_code = null;
      this.filters.device_id = null;
      this.filters.device_type_id = null;

      this.subscribeToPositions();

      if (this.isDeviceMode) {
        this.landmarkMarkers = [];
        this.filters.landmark_type_ids = [];

        if (!this.deviceTypes.length) {
          await this.loadDeviceTypes();
        }

        await this.loadDeviceFleet();
        this.reframeMap();

        return;
      }

      this.deviceFleet = [];
      await this.loadVehicleFleet();
      await this.loadLandmarks();
      this.reframeMap();
    },

    async handleDeviceFilterChange() {
      await this.loadDeviceFleet();
      this.reframeMap();
    },

    async loadDeviceTypes() {
      this.loadingStates.deviceTypes = true;
      try {
        const response = await axios.get(`${this.$store.state.api}deviceTypes`, {
          params: { paginated: false, limit: 200 },
        });
        const data = (response.data && response.data.data) || [];
        this.deviceTypes = Array.isArray(data) ? data : [];
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.deviceTypes = false;
      }
    },

    async loadDeviceFleet() {
      this.loadingStates.devices = true;
      try {
        const params = {
          ...(this.filters.center_id && { center_id: this.filters.center_id }),
          ...(this.filters.unit_id && { unit_id: this.filters.unit_id }),
          ...(this.filters.device_type_id && { device_type_id: this.filters.device_type_id }),
          ...(this.filters.device_id && { device_id: this.filters.device_id }),
        };

        const response = await axios.get(
          `${this.$store.state.api}device_last_updates/filtered`,
          { params },
        );

        this.deviceFleet = (response.data && response.data.data) || [];

        if (!this.filters.device_id && !this.filters.device_type_id) {
          this.devices = this.deviceFleet.map((device) => ({
            id: device.id,
            label: device.label,
          }));
        }
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.devices = false;
      }
    },

    onDevicePosition(payload) {
      const index = this.deviceFleet.findIndex((device) => device.id === payload.device_id);

      if (index === -1) {
        return;
      }

      const atualizado = {
        ...this.deviceFleet[index],
        is_primary: payload.is_primary,
        vehicle: payload.vehicle || this.deviceFleet[index].vehicle,
        position: {
          lat: payload.lat,
          long: payload.long,
          speed: payload.speed,
          device_battery_level: payload.device_battery_level,
          last_tracking_at: payload.last_tracking_at,
        },
      };

      this.deviceFleet.splice(index, 1, atualizado);
    },

    subscribeToPositions() {
      if (!window.Echo || !window.Echo.channel) {
        return;
      }

      this.unsubscribeFromPositions();

      if (this.isDeviceMode) {
        this.devicePositionListener = (payload) => this.onDevicePosition(payload);
        window.Echo.channel("device-last-update")
          .listen(".device-last-update", this.devicePositionListener);
        return;
      }

      this.lastUpdateListener = (payload) => {
        const data = Array.isArray(payload) ? payload[0] : payload;
        this.handleWebSocketUpdate(data);
      };
      window.Echo.channel("last-update").listen(".last-update", this.lastUpdateListener);
    },

    unsubscribeFromPositions() {
      if (!window.Echo || !window.Echo.channel) {
        return;
      }

      try {
        window.Echo.channel("last-update").stopListening(".last-update");
        window.Echo.channel("device-last-update").stopListening(".device-last-update");
      } catch (e) {
        window.console.warn("Fleet: falha ao encerrar canal de posicoes", e);
      }

      this.lastUpdateListener = null;
      this.devicePositionListener = null;
    },

    async loadCenters() {
      this.loadingStates.centers = true;
      try {
        const api = this.companyId
          ? `${this.$store.state.api}companies/centers/${this.companyId}`
          : `${this.$store.state.api}centers/`;
        const response = await axios.get(api);
        this.centers = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.centers = false;
      }
    },

    async loadUnits(centerId) {
      this.loadingStates.units = true;
      try {
        const response = await axios.get(
          `${this.$store.state.api}centers/unit/${centerId}`,
        );
        this.units = response.data || [];
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.units = false;
      }
    },

    async loadVehicleTypes() {
      this.loadingStates.vehicle_types = true;
      try {
        const resp = await axios.get(`${this.$store.state.api}vehicle_types`);
        const payload = resp && resp.data ? resp.data : [];
        const list = Array.isArray(payload) ? payload : payload.data || [];
        this.vehicleTypes = Array.isArray(list) ? list : [];
      } catch (e) {
        this.$error(e);
      } finally {
        this.loadingStates.vehicle_types = false;
      }
    },

    async loadVehicleFleet() {
      this.loadingStates.fleet = true;
      try {
        const params = {
          paginated: false,
          status: 1,
          ...(this.filters.vehicle_id && {
            vehicle_id: this.filters.vehicle_id,
          }),
          ...(this.filters.unit_id && { unit_id: this.filters.unit_id }),
          ...(this.filters.center_id && { center_id: this.filters.center_id }),
          ...(this.filters.vehicle_type_code && {
            vehicle_type_code: this.filters.vehicle_type_code,
          }),
        };

        const response = await axios.get(
          `${this.$store.state.api}vehicle_last_updates/filtered`,
          { params },
        );

        // com um veiculo filtrado a resposta vem com um item so; manter a lista do combo intacta
        if (!this.filters.vehicle_id) {
          this.vehicles = response.data.data || [];
        }

        let list = (response.data.data || []).filter(
          (vehicleData) =>
            vehicleData.vehicle && vehicleData.vehicle.status === 1,
        );

        this.vehicleFleet = list.map((vehicleData) => ({
          ...vehicleData,
          position:
            vehicleData.lat && vehicleData.long
              ? {
                  icon:
                    vehicleData.vehicle && vehicleData.vehicle.vehicle_type
                      ? vehicleData.vehicle.vehicle_type.file
                      : "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                  lat: parseFloat(vehicleData.lat),
                  lng: parseFloat(vehicleData.long),
                  speed: vehicleData.speed,
                  ignition_status: vehicleData.ignition_status,
                  port_status: vehicleData.port_status,
                  siren_status: vehicleData.siren_status,
                  device_battery_level: vehicleData.device_battery_level,
                }
              : null,
        }));

      } catch (error) {
        this.$message(null, error.response?.data || error.message, "error");
      } finally {
        this.loadingStates.fleet = false;
      }
    },

    reframeMap() {
      if (this.$refs.mapList && this.$refs.mapList.fitAll) {
        this.$refs.mapList.fitAll();
      }
    },

    handleWebSocketUpdate(vehicleUpdate) {
      if (!this.shouldUpdateVehicle(vehicleUpdate)) return;

      const updateId = Number(vehicleUpdate.vehicle_id);
      const vehicleIndex = this.vehicleFleet.findIndex(
        (fleetItem) => this.getRecordVehicleId(fleetItem) === updateId,
      );

      if (vehicleIndex !== -1) {
        const currentVehicle = this.vehicleFleet[vehicleIndex];

        if (this.isStaleUpdate(vehicleUpdate, currentVehicle)) return;

        const updatedVehicleData = this.createUpdatedVehicle(
          vehicleUpdate,
          currentVehicle,
        );
        this.$set(this.vehicleFleet, vehicleIndex, {
          ...currentVehicle,
          ...updatedVehicleData,
        });
      } else {
        // Ignore unknown ids from websocket to avoid cross-company leakage.
        // New rows are loaded by the API when filters are refreshed.
        return;
      }
    },

    shouldUpdateVehicle(vehicleUpdate) {
      const allowedIds = new Set(
        (this.vehicles || [])
          .map((vehicleData) => this.getRecordVehicleId(vehicleData))
          .filter((id) => id !== null),
      );

      if (!allowedIds.has(Number(vehicleUpdate.vehicle_id))) {
        return false;
      }

      const notMatchBasicFilters =
        (this.filters.vehicle_id &&
          Number(vehicleUpdate.vehicle_id) !==
            Number(this.filters.vehicle_id)) ||
        (this.filters.unit_id &&
          Number(vehicleUpdate.unit_id) !== Number(this.filters.unit_id)) ||
        (this.filters.center_id &&
          Number(vehicleUpdate.center_id) !== Number(this.filters.center_id));
      if (notMatchBasicFilters) return false;

      if (this.filters.vehicle_type_code) {
        const allowedIds = new Set(
          this.vehicleFleet
            .map((fleetItem) => this.getRecordVehicleId(fleetItem))
            .filter((id) => id !== null),
        );
        if (!allowedIds.has(Number(vehicleUpdate.vehicle_id))) return false;
      }

      return true;
    },

    isStaleUpdate(vehicleUpdate, currentVehicle) {
      const incoming = this.trackingTime(vehicleUpdate);
      const current = this.trackingTime(currentVehicle);

      if (incoming === null || current === null) return false;

      return incoming < current;
    },

    trackingTime(record) {
      const raw = record ? record.last_tracking_at : null;

      if (!raw) return null;

      const parsed = Date.parse(String(raw).replace(" ", "T"));

      return Number.isNaN(parsed) ? null : parsed;
    },

    createUpdatedVehicle(vehicleData, previousVehicle = null) {
      const iconFromPayload =
        vehicleData &&
        vehicleData.vehicle &&
        vehicleData.vehicle.vehicle_type &&
        vehicleData.vehicle.vehicle_type.file
          ? vehicleData.vehicle.vehicle_type.file
          : null;
      const iconFromPrevious =
        previousVehicle && previousVehicle.position
          ? previousVehicle.position.icon
          : null;

      return {
        ...vehicleData,
        position:
          vehicleData.lat && vehicleData.long
            ? {
                icon:
                  iconFromPayload ||
                  iconFromPrevious ||
                  "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                lat: parseFloat(vehicleData.lat),
                lng: parseFloat(vehicleData.long),
                speed: vehicleData.speed,
                ignition_status: vehicleData.ignition_status,
                port_status: vehicleData.port_status,
                siren_status: vehicleData.siren_status,
                device_battery_level: vehicleData.device_battery_level,
              }
            : previousVehicle
              ? previousVehicle.position
              : null,
      };
    },
  },

  async mounted() {
    if (this.centerId) {
      this.filters.center_id = this.centerId;
    }

    if (this.unitId) {
      this.filters.unit_id = this.unitId;
    }

    await this.loadCenters();
    await this.loadVehicleTypes();
    await this.loadLandmarkTypes();

    if (this.filters.center_id) {
      await this.loadUnits(this.filters.center_id);
    }

    await this.loadVehicleFleet();
    await this.loadLandmarks();

    this.subscribeToPositions();
  },

  beforeDestroy() {
    this.unsubscribeFromPositions();
  },
};
</script>

<style scoped>
.custom-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
  vertical-align: middle;
  cursor: pointer;
  outline: none;
}
.custom-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ccc;
  transition: background 0.2s;
  border-radius: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.custom-toggle input:checked + .slider {
  background: #4caf50;
}
.slider:before {
  content: "";
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  transition: left 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.18);
}
.custom-toggle input:checked + .slider:before {
  left: 23px;
}
.custom-toggle:focus .slider,
.custom-toggle:focus-visible .slider {
  box-shadow: 0 0 0 2px #1976d2;
}
.toggle-label {
  user-select: none;
  font-size: 1rem;
  color: #444;
  margin-left: 2px;
}
.vselect {
  width: 100%;
}
</style>
